import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { axiosInstance } from '../../helpers'
import {
    LeaseAuditEvent,
    DeliverableDetail,
    DeliverableValue,
} from '../../models'

export const useDeliverable = (leaseAuditId: number) => {
    const queryClient = useQueryClient()
    const deliverableQueryKey = ['deliverable', leaseAuditId] as const

    const {
        data: deliverable,
        isLoading: isLoadingDeliverable,
        error: errorDeliverable,
    } = useQuery<DeliverableDetail>({
        queryKey: deliverableQueryKey,
        queryFn: async () => {
            const res = await axiosInstance.get<DeliverableDetail>(
                `/document-audit/deliverable/latest/`,
                {
                    params: {
                        lease_audit_id: leaseAuditId,
                    },
                },
            )
            return res.data
        },
        enabled: !!leaseAuditId, // Only run the query if we have an leaseAuditId
    })

    const updateDeliverableValueMessageCount = (
        rowId: number,
        valueId: number,
        newCount: number,
    ) => {
        queryClient.setQueryData<DeliverableDetail>(
            deliverableQueryKey,
            (oldData) => {
                if (!oldData) return oldData

                return {
                    ...oldData,
                    resident_term_directories: oldData.resident_term_directories.map(
                        (row) => {
                            if (row.id !== rowId) return row

                            return {
                                ...row,
                                values: row.values.map((value) => {
                                    if (value.id !== valueId) return value

                                    return {
                                        ...value,
                                        message_count: newCount,
                                    }
                                }),
                            }
                        },
                    ),
                }
            },
        )
    }

    // Mutation for bulk accepting deliverable values
    const {
        mutateAsync: bulkAcceptDeliverableValues,
        isLoading: isBulkAccepting,
    } = useMutation({
        mutationFn: async ({
            deliverableId,
            deliverableValueIds,
            notes,
        }: {
            deliverableId: number
            deliverableValueIds: number[]
            notes: string
        }) => {
            const response = await axiosInstance.post(
                `/document-audit/deliverable/bulk-accept/`,
                // `/document-audit/deliverable/${deliverableId}/bulk_accept/`,
                {
                    deliverable_value_ids: deliverableValueIds,
                    notes: notes,
                },
                {
                    params: {
                        lease_audit_id: leaseAuditId,
                    },
                },
            )
            return response.data
        },
        onSuccess: () => {
            // Invalidate the deliverable query to refetch the data
            queryClient.invalidateQueries({ queryKey: deliverableQueryKey })
        },
    })

    return {
        deliverable,
        isLoadingDeliverable,
        errorDeliverable,
        deliverableQueryKey,
        updateDeliverableValueMessageCount,
        bulkAcceptDeliverableValues,
        isBulkAccepting,
    }
}
